import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Layout.module.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Prevent body scroll when mobile menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className={styles.layout}>
      <nav className={`${styles.navbar} ${isScrolled ? styles['navbar-scrolled'] : ''}`}>
        <div className={styles['nav-container']}>
          <Link to="/" className={styles['nav-logo']}>
            <span className={styles['logo-text']}>jagoalt</span>
          </Link>
          
          <AnimatePresence>
            <motion.div 
              className={`${styles['nav-links']} ${isMobileMenuOpen ? styles['nav-links-open'] : ''}`}
              initial={false}
              animate={isMobileMenuOpen ? { opacity: 1, height: 'auto' } : {}}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/" 
                  className={`${styles['nav-link']} ${isActive('/') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/projects" 
                  className={`${styles['nav-link']} ${isActive('/projects') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Projects
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/evolution" 
                  className={`${styles['nav-link']} ${isActive('/evolution') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Journey
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/about" 
                  className={`${styles['nav-link']} ${isActive('/about') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/contact" 
                  className={`${styles['nav-link']} ${styles['cta-link']} ${isActive('/contact') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          <button 
            className={`${styles['mobile-menu-toggle']} ${isMobileMenuOpen ? styles.open : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>
      
      <main className={styles['main-content']}>
        {children}
      </main>
      
      <footer className={styles.footer}>
        <div className={styles['footer-container']}>
          <div className={styles['footer-content']}>
            <div className={styles['footer-brand']}>
              <h3>jagoalt</h3>
              <p>Web design & development studio</p>
            </div>
            <div className={styles['footer-links']}>
              <Link to="/contact">Contact</Link>
              <a href="https://github.com/joshhicks7" target="_blank" rel="noopener noreferrer">GitHub</a>
              <a href="https://linkedin.com/in/josh--hicks" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </div>
          </div>
          <div className={styles['footer-bottom']}>
            <p>&copy; 2026 Jagoalt. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;